import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import LookbookImage, {
  ActionButton,
  BackButton,
} from '@fuww/library/src/Lookbook/Image';
import DeleteIcon from '@fuww/library/src/Icons/Delete';
import EditIcon from '@fuww/library/src/Icons/Edit';
import DangerIcon from '@fuww/library/src/Icons/Danger';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import Image from '../../Image';
import { useSiteContext } from '../../SiteContext';
import { UserContext } from '../../UserProvider';
import Schema from '../Schema';
import shareList from '../../../lib/lookbook/shareData';
import { getRouter } from '../../../lib/lookbook/previousListRoute';
import removeImageMutation
  from '../../../lib/queries/lookbook/removeImage.graphql';
import { allImageConfigurations } from '../../../lib/imageConfigurations';
import { Router } from '../../../routes.mjs';
import LinkAndAnchor from '../../LinkAndAnchor';
import messages from '../../../lib/messages.json';

dayjs.extend(localizedFormat);

const ImageDetail = ({
  id,
  title,
  description,
  imageUrls: originalImageUrls,
  insertedAt,
  credits,
  creator,
  category,
  uploadUrl,
}) => {
  const [metaImageUrl, pinterestImageUrl, ...imageUrls] = originalImageUrls;
  const { title: categoryTitle } = { ...category };
  const { url, dashboard } = useSiteContext();
  const { user, roles } = useContext(UserContext);
  const [removeButtonDisabled, setRemoveButtonDisabled] = useState(false);
  const [removeImage] = useMutation(removeImageMutation, {
    onCompleted: () => {
      const { route, params } = getRouter();

      Router.pushRoute(dashboard ? 'dashboardCategory' : route, params);
    },
  });
  const intl = useIntl();

  return (
    <>
      <LookbookImage
        image={(
          <Image
            placeholder={imageUrls[0]}
            src={imageUrls[6]}
            srcSet={`${imageUrls[1]} 328w, ${
              imageUrls[2]} 477w, ${
              imageUrls[3]} 656w, ${
              imageUrls[4]} 849w, ${
              imageUrls[5]} 954w, ${
              imageUrls[6]} 1698w`}
            sizes={`
              (min-width: ${breakpoints.md}px)
                calc((100vw - 2rem) * 0.62 - 1.5rem),
                calc(100vw - 2rem)
            `}
            width="100%"
            alt={title || 'Untitled'}
          />
        )}
        title={title}
        description={description}
        createdAt={dayjs(insertedAt).format('ll')}
        credits={credits}
        creditsLabel={intl.formatMessage(messages['image.credits'])}
        name={creator?.name}
        categoryTitle={categoryTitle}
        originalUrl={
          user && uploadUrl
          // eslint-disable-next-line no-underscore-dangle
          && roles?.__global_roles__?.includes('internalImages')
            ? uploadUrl : null
        }
        removeButton={
          user
          // eslint-disable-next-line no-underscore-dangle
          && roles?.__global_roles__?.includes('removeImage')
            ? (
              <>
                <ActionButton
                  title="remove including the file and purge cache"
                  disabled={removeButtonDisabled}
                  onClick={() => {
                    setRemoveButtonDisabled(true);
                    removeImage({
                      variables: {
                        id,
                        removeFile: true,
                        purgeResponsiveImageConfigurations:
                          allImageConfigurations,
                      },
                    });
                  }}
                >
                  <DangerIcon />
                </ActionButton>
                <ActionButton
                  title="remove"
                  disabled={removeButtonDisabled}
                  onClick={() => {
                    setRemoveButtonDisabled(true);
                    removeImage({ variables: { id } });
                  }}
                >
                  <DeleteIcon />
                </ActionButton>
              </>
            ) : null
        }
        shareData={shareList(url, metaImageUrl, pinterestImageUrl)}
        backButton={(
          <LinkAndAnchor
            route={dashboard ? 'dashboardCategory' : 'lookbook'}
            params={dashboard ? { slug: 'logo', categoryId: '126' } : {}}
          >
            <BackButton color="secondary">
              {intl.formatMessage(messages.lookbookBackButtonLabel)}
            </BackButton>

          </LinkAndAnchor>
        )}
        updateButton={
          user
          // eslint-disable-next-line no-underscore-dangle
          && roles?.__global_roles__?.includes('updateImage')
            ? (
              <LinkAndAnchor
                route="lookbookImageUpdate"
                params={{
                  id,
                }}
              >
                <ActionButton
                  title="update"
                >
                  <EditIcon />
                </ActionButton>
              </LinkAndAnchor>
            ) : null
          }
      />
      <Schema
        description={description}
        title={title}
        imageUrls={[metaImageUrl, ...imageUrls]}
        insertedAt={insertedAt}
        creator={creator}
      />
    </>
  );
};

ImageDetail.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrls: PropTypes.arrayOf(PropTypes.string),
  insertedAt: PropTypes.string,
  credits: PropTypes.string,
  creator: PropTypes.shape({ name: PropTypes.string }),
  category: PropTypes.shape({}),
  uploadUrl: PropTypes.string,
};

ImageDetail.defaultProps = {
  title: null,
  description: null,
  imageUrls: [],
  insertedAt: null,
  credits: null,
  creator: {},
  category: {},
  uploadUrl: null,
};

export default ImageDetail;
